export interface Replacement {
  find: string
  replace: string
  lineBounded?: true
  endOfFile?: true
  oldStartLine?: number
  /** Old lines the hunk consumed. Only 1 can mean a genuine empty line. */
  oldLineCount?: number
}

interface LineBoundedMatch {
  start: number
  end: number
  lineEnding: '\n' | '\r\n'
}

function lineBoundedMatch(
  content: string,
  find: string,
  endOfFile: boolean,
  expectedLine?: number,
): LineBoundedMatch | undefined {
  let normalized = content
  let originalOffsets: number[] | undefined
  if (content.includes('\r')) {
    normalized = ''
    originalOffsets = [0]
    for (let index = 0; index < content.length; index++) {
      if (content[index] === '\r' && content[index + 1] === '\n') continue
      normalized += content[index]
      originalOffsets.push(index + 1)
    }
  }

  const candidates: number[] = []
  if (find.length === 0) {
    // An empty line carries no text to search for, so position is the only evidence.
    if (expectedLine === undefined) return undefined
    let index = 0
    let currentLine = 1
    while (currentLine < expectedLine && index < normalized.length) {
      if (normalized[index] === '\n') currentLine++
      index++
    }
    if (currentLine !== expectedLine) return undefined
    // An empty line is a newline at this position. End-of-content is not another
    // line, and accepting it would append text the file never had.
    if (normalized[index] !== '\n') return undefined
    if (endOfFile && index + 1 !== normalized.length) return undefined
    candidates.push(index)
  } else {
    for (let index = normalized.indexOf(find); index !== -1; index = normalized.indexOf(find, index + 1)) {
      const startsAtLineBoundary = index === 0 || normalized[index - 1] === '\n'
      const end = index + find.length
      const endsAtLineBoundary = end === normalized.length || normalized[end] === '\n'
      const endsAtFileBoundary =
        end === normalized.length || (end + 1 === normalized.length && normalized[end] === '\n')
      if (!startsAtLineBoundary || !endsAtLineBoundary || (endOfFile && !endsAtFileBoundary)) continue
      candidates.push(index)
      if (expectedLine === undefined && candidates.length > 1) return undefined
    }
  }
  if (candidates.length === 0) return undefined

  let normalizedMatch: number | undefined = candidates[0]
  if (candidates.length > 1 && expectedLine !== undefined) {
    normalizedMatch = undefined
    let currentLine = 1
    let scannedThrough = 0
    for (const index of candidates) {
      for (; scannedThrough < index; scannedThrough++) {
        if (normalized[scannedThrough] === '\n') currentLine++
      }
      if (currentLine === expectedLine) {
        normalizedMatch = index
        break
      }
    }
  }
  if (normalizedMatch === undefined) return undefined

  const start = originalOffsets === undefined ? normalizedMatch : originalOffsets[normalizedMatch]
  const end =
    originalOffsets === undefined ? normalizedMatch + find.length : originalOffsets[normalizedMatch + find.length]
  if (start === undefined || end === undefined) return undefined
  const matchedContent = content.slice(start, end)
  const hasCrLf = matchedContent.includes('\r\n')
  const hasBareLf = /(^|[^\r])\n/.test(matchedContent)
  if (hasCrLf && hasBareLf) return undefined
  const adjacentLineEnding =
    hasCrLf || content.slice(end, end + 2) === '\r\n' || content.slice(Math.max(0, start - 2), start) === '\r\n'
  return {start, end, lineEnding: adjacentLineEnding ? '\r\n' : '\n'}
}

export function applyReplacements(content: string, replacements: Replacement[]): string | undefined {
  let updated = content
  let lineOffset = 0
  for (const replacement of replacements) {
    // Consuming no old line is a pure insertion, not a replacement: applying it
    // as one would overwrite whatever sits at that position.
    if (replacement.find.length === 0 && !(replacement.lineBounded && replacement.oldLineCount === 1)) {
      return undefined
    }
    if (replacement.lineBounded) {
      const expectedLine = replacement.oldStartLine === undefined ? undefined : replacement.oldStartLine + lineOffset
      const match = lineBoundedMatch(updated, replacement.find, replacement.endOfFile === true, expectedLine)
      if (match === undefined) return undefined
      const replacementText = replacement.replace.replaceAll('\n', match.lineEnding)
      updated = `${updated.slice(0, match.start)}${replacementText}${updated.slice(match.end)}`
      lineOffset += replacement.replace.split('\n').length - replacement.find.split('\n').length
    } else {
      const index = updated.indexOf(replacement.find)
      if (index === -1 || updated.indexOf(replacement.find, index + 1) !== -1) return undefined
      updated = `${updated.slice(0, index)}${replacement.replace}${updated.slice(index + replacement.find.length)}`
    }
  }
  return updated
}
